import moment from 'moment';

// Backdate window (in business days) is supplied via an environment variable so it can be
// tuned per environment without a redeploy (the app just needs a restart). If the value is
// missing or invalid we fall back to the agreed default of 6 business days.
const DEFAULT_BACKDATE_BUSINESS_DAYS = 6;
const FUTURE_DATE_LIMIT_DAYS = 15;

export const ACCOUNTING_PERIOD_WARNING = {
    title: 'Confirm Accounting Period',
    body: 'You selected a prior Invoice Date and/or Posting Date. Confirm the accounting period for the selected date is still open before continuing. If the accounting period is closed, the invoice may fail to post to Workday.',
};

export const getInvoiceBackdateBusinessDays = () => {
    const parsed = parseInt(process.env.REACT_APP_INVOICE_BACKDATE_BUSINESS_DAYS, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_BACKDATE_BUSINESS_DAYS;
};

const isWeekend = (m) => {
    const dayOfWeek = m.day();
    return dayOfWeek === 0 || dayOfWeek === 6;
};

/**
 * Walks backward from `fromDate` (exclusive of that day) until `businessDays` weekday
 * dates have been counted. Weekends are skipped; holidays are not considered.
 */
export const subtractBusinessDays = (fromDate, businessDays) => {
    const cursor = moment(fromDate).startOf('day');
    if (!cursor.isValid() || businessDays <= 0) {
        return cursor;
    }

    let remaining = businessDays;
    // Cap the search so a bad input can never spin forever.
    for (let i = 0; i < 366 && remaining > 0; i += 1) {
        cursor.subtract(1, 'day');
        if (!isWeekend(cursor)) {
            remaining -= 1;
        }
    }

    return cursor;
};

export const getMinimumAllowedInvoiceDate = (now = moment(), backdateDays) => {
    const days = Number.isFinite(backdateDays) ? backdateDays : getInvoiceBackdateBusinessDays();
    return subtractBusinessDays(now, days);
};

export const getMaximumAllowedInvoiceDate = (now = moment()) =>
    moment(now).add(FUTURE_DATE_LIMIT_DAYS, 'days').endOf('day');

export const validateInvoiceDateRange = (selectedDate, label, now = moment()) => {
    if (!selectedDate) return `${label} is required.`;

    const minDate = getMinimumAllowedInvoiceDate(now);
    const maxDate = getMaximumAllowedInvoiceDate(now);

    if (!selectedDate.isBetween(minDate, maxDate, 'day', '[]')) {
        if (selectedDate.isBefore(minDate, 'day')) {
            return `${label} cannot be before ${minDate.format('MM/DD/YYYY')}.`;
        }
        return `${label} cannot be more than ${FUTURE_DATE_LIMIT_DAYS} days in the future.`;
    }

    return true;
};

/** True when either date is earlier than today (calendar day). */
export const hasPriorInvoiceOrPostingDate = (invoiceDate, postingDate, now = moment()) => {
    const today = moment(now).startOf('day');
    return [invoiceDate, postingDate].some(
        (date) => date && moment(date).isValid() && moment(date).isBefore(today, 'day')
    );
};
